import {ConsumptionTotals, CostByCurrency} from 'src/types';

/** Costs and prices cross the wire in 1/10000 units of their currency. */
const UNITS_PER_CURRENCY = 10000;

const format = (value: number, digits: number) =>
    value.toLocaleString(undefined, {minimumFractionDigits: digits, maximumFractionDigits: digits});

export interface MoneyAmount {
    /** The figure, formatted. */
    value: string;
    /** The currency code and any suffix. Absent only when the server named no currency at all. */
    unit?: string;
}

/**
 * The money in a report, as a list of figures that are each safe to print.
 *
 * A report covers whatever locations the filter admits, and each prices in its own currency, so there
 * is not always one number to show. The server answers with one entry per currency, and this returns
 * one figure per entry - always at least one, so a single currency and three are the same shape and
 * the caller styles them identically. It used to join several into one string instead, which is why a
 * two-currency total was rendered as a sentence in the big type while a one-currency total was a
 * figure with a small unit beside it.
 *
 * `fallback` is the business's own currency, used only when nothing matched at all and there is no
 * entry to read a code from. It replaces the hardcoded CHF that four of these components carried,
 * which is how a business pricing in hryvnia came to see francs. It is deliberately NOT used for an
 * entry the server sent without a currency: that is unattributable money, and printing the business
 * code over it is what made one CHF total read as two CHF figures side by side. Such an entry shows
 * its figure bare, so the defect is visible rather than disguised as a second currency.
 */
export const totalCost = (data: ConsumptionTotals | undefined, fallback: string): MoneyAmount[] =>
    describe(data?.costs, fallback, (entry) => entry.cost / UNITS_PER_CURRENCY, 2, '');

export const averagePrice = (data: ConsumptionTotals | undefined, fallback: string): MoneyAmount[] =>
    describe(data?.costs, fallback, (entry) => (entry.averagePrice ?? 0) / UNITS_PER_CURRENCY, 3, '/kWh');

const describe = (
    costs: CostByCurrency[] | undefined,
    fallback: string,
    pick: (entry: CostByCurrency) => number,
    digits: number,
    suffix: string
): MoneyAmount[] => {
    // A currency that holds no money has nothing to say about money. The server already drops those,
    // and this keeps a report-service that has not been redeployed yet from printing the unpriced
    // sessions' currency-less zero beside the real figure.
    const entries = (costs ?? []).filter((e) => e.cost !== 0);

    // Nothing matched is not an absence of money, it is zero - and zero of the business's own currency
    // is the only honest code to put on it.
    if (entries.length === 0) {
        return [{value: format(0, digits), unit: `${fallback}${suffix}`}];
    }

    return entries.map((e) => ({
        value: format(pick(e), digits),
        unit: e.currency ? `${e.currency}${suffix}` : undefined,
    }));
};

/**
 * The code to print beside a breakdown row's cost.
 *
 * A row is single-currency by construction - the server splits them rather than mixing - so it either
 * names one or carries no money worth a code. A row with money and no currency is a defect in the
 * stored event, not a row in the business's currency, so it gets no code: see {@link totalCost}.
 */
export const rowCurrency = (row: {currency?: string; cost?: number | null}, fallback: string): string =>
    row.currency ?? (row.cost ? '' : fallback);
